import React, { createContext, FC, memo, ReactNode, useContext, useEffect, useState } from 'react';

import { Snackbar } from 'src/shared/components';
import { useTranslate } from 'src/shared/providers/TranslationProvider';

import { ConfirmationSnackbar } from './ConfirmationSnackbar';

const DEFAULT_DURATION = 3000;

type SnackDisplayCallbackType = (
  messages: { confirmation: string; success?: string; undo?: string },
  onSubmit: () => Promise<any> | any,
  onUndo?: () => Promise<void> | any,
  duration?: number,
) => void;

type ConfirmationSnackbarProps = { show: SnackDisplayCallbackType };

const ConfirmationSnackbarContext = createContext<ConfirmationSnackbarProps>({} as ConfirmationSnackbarProps);

export const useConfirmationSnackBar = (): ConfirmationSnackbarProps =>
  useContext<ConfirmationSnackbarProps>(ConfirmationSnackbarContext);

interface IState {
  confirmMessage: string;
  successMessage?: string;
  undoMessage?: string;
  duration: number;
  confirming: boolean;
  success: boolean;
  undo: boolean;
  onSubmit: () => Promise<void> | void;
  onUndo: () => Promise<void> | void;
}

const initialState: IState = {
  confirmMessage: '',
  confirming: false,
  success: false,
  undo: false,
  duration: 0,
  onSubmit: () => {},
  onUndo: () => {},
};

export const ConfirmationSnackbarProvider: FC<{ children: ReactNode }> = memo(({ children }) => {
  const { t } = useTranslate();
  const [state, setState] = useState<IState>(initialState);

  const show = (
    messages: { confirmation: string; success?: string; undo?: string },
    onSubmit: () => Promise<void> | void,
    onUndo: () => Promise<void> | void = () => {},
    duration = DEFAULT_DURATION,
  ) => {
    setState({
      ...initialState,
      confirmMessage: messages.confirmation,
      successMessage: messages.success,
      undoMessage: messages.undo,
      duration,
      onUndo: () => onUndo(),
      onSubmit: () => onSubmit(),
      confirming: true,
    });
  };

  const handleClose = async () => {
    setState((prevState) => ({ ...prevState, confirming: false, success: true }));
    await state.onSubmit();
  };

  const handleUndo = async () => {
    setState((prevState) => ({ ...prevState, confirming: false, undo: true }));
    await state.onUndo();
  };

  const handleReset = () => {
    setState({ ...initialState });
  };

  useEffect(() => {
    if (!state.confirming) {
      return;
    }

    const handleTabClose = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      return (event.returnValue = t('global.changesYouMadeMayNotBeSaved'));
    };

    window.addEventListener('beforeunload', handleTabClose);

    return () => {
      window.removeEventListener('beforeunload', handleTabClose);
    };
  }, [state.confirming]);

  return (
    <ConfirmationSnackbarContext.Provider value={{ show }}>
      {children}
      <ConfirmationSnackbar
        open={state.confirming}
        message={state.confirmMessage}
        duration={state.duration}
        onUndo={handleUndo}
        onClose={handleClose}
      />
      {state.successMessage && (
        <Snackbar open={state.success} message={state.successMessage} onClose={handleReset} variant="success" />
      )}
      {state.undoMessage && <Snackbar open={state.undo} message={state.undoMessage} onClose={handleReset} />}
    </ConfirmationSnackbarContext.Provider>
  );
});
